/* eslint-disable react/no-danger */
/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/prop-types */
import React from "react";
import { string, arrayOf, object, shape, oneOf, bool, oneOfType } from "prop-types";
import classNames from "classnames/bind";
import showdown from "showdown";
// eslint-disable-next-line import/no-cycle
import { Button } from "../Button";
// eslint-disable-next-line import/no-cycle
import { Link } from "../../sharedComponents/Link";
import { Icon } from "../Icons";
import * as styles from "./Table.module.scss";

const cx = classNames.bind(styles);

const markdownToHtml = str => {
	const converter = new showdown.Converter();
	if (typeof str === "string") return converter.makeHtml(str).replace(/\\n/g, "<br/>");
	return str;
};

const Cell = ({ content, config, renderComponent }) => {
	const type = typeof content;
	switch (type) {
		case "boolean":
			return content ? (
				<Icon name="checked--o" className={styles.icon} />
			) : (
				<Icon name={config?.boolIcon || "close--o"} className={cx(styles.icon, styles.minus)} />
			);
		case "object":
			if (content.type === "button") {
				return renderComponent.button(content);
			}
			if (content.type === "link") {
				return renderComponent.link(content);
			}
			if (content.type === "list") {
				return renderComponent.list(content.list);
			}
			if (content.type === "string") {
				return <div dangerouslySetInnerHTML={{ __html: markdownToHtml(content.text) }} />;
			}
			if (content.icon) {
				return renderComponent.withIcon(content);
			}
			return <div dangerouslySetInnerHTML={{ __html: markdownToHtml(content.text) }} />;

		default:
			return <div dangerouslySetInnerHTML={{ __html: markdownToHtml(content) }} />;
	}
};

const hasTag = header => {
	for (let i = 0; i < header.length; i++) {
		if (header[i].tag) return true;
	}
	return false;
};

const defaultRenderComponent = {
	button: button => (
		<Button to={button.to} target={button.target}>
			{button.text}
		</Button>
	),
	link: link => (
		<Link to={link.to} target={link.target}>
			{link.text}
		</Link>
	),
	list: list =>
		list?.map((item, index) => (
			// eslint-disable-next-line react/no-array-index-key
			<div key={index} className={styles.listItem}>
				<Icon name="checked--o" className={cx(styles.listItemCheck, styles.listItemCheckSuccess)} />
				{item}
			</div>
		)),
	withIcon: item => (
		<div className={styles.cellWithIcon}>
			<Icon
				name={item.icon}
				className={
					item.icon === "checked--o" ? styles.listItemCheckSuccess : styles.listItemCheckError
				}
			/>
			<p>{item.text}</p>
		</div>
	),
};

const ModifiedText = ({ text }) => {
	const regex = /(\*{2}.+?\*{2})/g;

	const surroundWithStrong = (match, index) => (
		<strong key={index} className={styles.headerHighlight}>
			{match.replaceAll("**", "")}
		</strong>
	);

	// Replace matched substrings with <span> tags
	const modifiedText = text
		?.split(regex)
		.map((part, index) => (regex.test(part) ? surroundWithStrong(part, index) : part));

	return <p>{modifiedText}</p>;
};

const Table = ({
	header,
	rows,
	footer,
	subtext,
	headerStyle,
	tableStyle,
	className,
	config,
	variant,
	renderComponent: providedRenderComponent,
}) => {
	const renderComponent = {
		...defaultRenderComponent,
		...providedRenderComponent,
	};

	return (
		<div className={cx("tableContainer", className)} style={{ overflowX: "auto" }}>
			<table className={cx(styles.table, variant)} style={tableStyle}>
				<thead>
					{hasTag(header) && (
						<tr>
							{header?.map(i => (
								<th
									key={i.tag}
									className={cx("tag", {
										background: i.background || headerStyle?.background,
									})}>
									{i.tag && <span>{i.tag}</span>}
								</th>
							))}
						</tr>
					)}
					<tr>
						{header?.map((i, index) => (
							<th
								key={i.title || index}
								className={cx(
									styles.header,
									index === 0 && config?.stickyIndex && styles.sticky,
									(i.background || headerStyle?.background) && styles.background,
									(i.bold || headerStyle?.bold) && styles.bold
								)}
								style={{
									width: i.width,
									textAlign: i.textAlign,
									padding: i.padding,
									verticalAlign: i.padding,
									...(!i.background ? { background: i?.columnBackground } : {}),
								}}>
								<ModifiedText text={i.title} />
								{i.subTitle && <span>{i.subTitle}</span>}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{rows?.map((r, index) => (
						<tr key={r.title || index} className={cx(r.background && styles.background)}>
							{r.columns?.map((c, index) =>
								c !== null ? (
									<td
										colSpan={c?.colSpan}
										rowSpan={c?.rowSpan}
										// eslint-disable-next-line react/no-array-index-key
										key={index}
										className={cx(
											styles.cell,
											index === 0 && config?.stickyIndex && styles.sticky,
											(r.background || c.background) && styles.background,
											(header[index]?.bold || r.bold || c.bold === true) && styles.bold,
											(header[index]?.small || r.small || c.small === true) && styles.small
										)}
										style={{
											textAlign: header[index]?.textAlign || c.textAlign,
											verticalAlign: header[index]?.verticalAlign || c.verticalAlign,
											background: header[index]?.columnBackground,
											color: c.textColor || r.textColor,
											...(index !== 0 ? { fontSize: c.fontSize || r.fontSize } : {}),
										}}>
										<Cell content={c} config={config} renderComponent={renderComponent} />
									</td>
								) : null
							)}
						</tr>
					))}
				</tbody>
				{footer && (
					<tfoot>
						{footer.map(f => (
							<tr className={cx(f.background && styles.background)}>
								{f.columns?.map(c => (
									<td
										colSpan={c?.colSpan}
										rowSpan={c?.rowSpan}
										className={cx("footer", {
											background: c.background || headerStyle?.background,
										})}
										style={{
											color: c.textColor || f.textColor,
										}}>
										<div>
											{c.text.map(t => (
												<p>
													<ModifiedText text={t} />
												</p>
											))}
										</div>
									</td>
								))}
							</tr>
						))}
					</tfoot>
				)}
			</table>
			{/* eslint-disable-next-line react/no-danger */}
			{subtext && <div className={cx("subtext")} dangerouslySetInnerHTML={{ __html: subtext }} />}
		</div>
	);
};

Table.propTypes = {
	rows: arrayOf(
		shape({
			small: bool,
			background: bool,
			bold: bool,
			columns: arrayOf(oneOfType([string, bool, object])),
		})
	).isRequired,
	header: arrayOf(
		shape({
			title: string,
			subTitle: string,
			width: string,
			textAlign: oneOf(["left", "right", "center"]),
			padding: string,
			verticalAlign: oneOf(["top", "bottom", "middle"]),
			columnBackground: string,
			background: bool,
			bold: bool,
		})
	),
	footer: arrayOf(
		shape({
			title: string,
			background: bool,
		})
	),
	headerStyle: shape({
		small: bool,
		background: bool,
		bold: bool,
	}),
	tableStyle: shape({
		minWidth: string,
	}),
	subtext: string,
	variant: oneOf(["", "modern"]),
	className: string,
	config: shape({
		boolIcon: string,
		stickyIndex: bool,
	}),
};
Table.defaultProps = {
	subtext: null,
	headerStyle: null,
	tableStyle: null,
	className: "",
	config: null,
	variant: "",
	footer: null,
	header: [],
};

export default Table;
export { Table };
